import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import {
  deleteModelCache,
  evictModelCache,
  getModelImportStatus,
  getModelCacheStatus,
  importModelCache,
  scanModelCache,
} from "../../../hooks/useModelCaches";
import { formatBytes } from "./ModelCard";
import {
  FONT,
  HAIRLINE,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  WEIGHT,
} from "../../../theme/tokens";

function formatDateTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function countText(items) {
  const count = Array.isArray(items) ? items.length : 0;
  return count.toLocaleString();
}

function formatScanResponse(payload) {
  return [
    `Scan checked ${countText(payload?.checked_models)} models`,
    `discovered ${countText(payload?.discovered_models)}`,
    `imported ${countText(payload?.imported_models)}`,
    `updated ${countText(payload?.updated_models)}`,
    `skipped ${Object.keys(payload?.skipped_models || {}).length.toLocaleString()}`,
  ].join(", ");
}

function formatEvictionResponse(payload) {
  const candidateCount = Array.isArray(payload?.candidates)
    ? payload.candidates.length
    : 0;
  return [
    payload?.dry_run ? "Eviction dry run complete" : "Eviction complete",
    `${candidateCount.toLocaleString()} candidate${candidateCount === 1 ? "" : "s"}`,
    `${formatBytes(payload?.started_size_bytes)} -> ${formatBytes(payload?.final_size_bytes)}`,
    `target ${formatBytes(payload?.target_size_bytes)}`,
  ].join("; ");
}

function formatImportResponse(payload) {
  return `Import ${payload?.repo_id || "model"} at ${payload?.revision || "main"}: ${payload?.message || ""}`.trim();
}

function formatImportStatusResponse(payload) {
  const filesText =
    payload?.files_total == null
      ? `${Number(payload?.files_downloaded || 0).toLocaleString()} files`
      : `${Number(payload?.files_downloaded || 0).toLocaleString()} of ${Number(payload.files_total || 0).toLocaleString()} files`;
  return [
    [
      "Model",
      `${payload?.repo_id || "model"} at ${payload?.revision || "main"}`,
    ],
    ["State", payload?.state || "unknown"],
    ["Files", filesText],
    ["Message", payload?.message || "No status message."],
    payload?.error ? ["Error", payload.error] : null,
  ].filter(Boolean);
}

function AlertMessage({ message }) {
  if (!Array.isArray(message)) return message;

  return (
    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
      {message.map(([label, value]) => (
        <Box component="li" key={label} sx={{ mb: 0.25 }}>
          <Typography component="span" sx={{ fontWeight: WEIGHT.semibold }}>
            {label}:
          </Typography>{" "}
          {value}
        </Box>
      ))}
    </Box>
  );
}

AlertMessage.propTypes = {
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)),
  ]).isRequired,
};

function formatDeleteModelResponse(payload) {
  const freed = formatBytes(payload?.freed_bytes);
  const revisionText = payload?.revision ? ` revision ${payload.revision}` : "";
  const deletedCount = Array.isArray(payload?.deleted_revisions)
    ? payload.deleted_revisions.length
    : 0;
  const deletedText =
    deletedCount > 0
      ? ` Deleted ${deletedCount} revision${deletedCount === 1 ? "" : "s"}.`
      : "";
  const movedText = payload?.moved_path
    ? ` Moved to ${payload.moved_path}.`
    : "";

  return `Deleted ${payload?.repo_id || "model"}${revisionText}; freed ${freed}.${deletedText}${movedText}`;
}

function StatusField({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        title={String(value)}
        sx={{
          fontSize: FONT.sm,
          color: INK,
          fontWeight: WEIGHT.medium,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

StatusField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

const noop = () => {};
const IMPORT_POLL_INTERVAL_MS = 5000;

const ModelAdminPanel = forwardRef(function ModelAdminPanel(
  {
    onRefreshModels,
    refreshSignal = 0,
    onModelDeleted = noop,
    onDeletingRepoChange = noop,
  },
  ref,
) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState("");
  const [actionAlert, setActionAlert] = useState(null);
  const [repoId, setRepoId] = useState("");
  const [revision, setRevision] = useState("main");
  const [forceDownload, setForceDownload] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [deletingRepo, setDeletingRepo] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const importPollTimerRef = useRef(null);
  const pollImportStatusRef = useRef(null); // Function to poll import status

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError("");
    try {
      setStatus(await getModelCacheStatus());
    } catch (err) {
      console.error("Failed to load model cache status:", err);
      setStatusError(err.message || "Failed to load model cache status.");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (refreshSignal > 0) {
      loadStatus();
    }
  }, [loadStatus, refreshSignal]);

  useEffect(() => {
    onDeletingRepoChange(deletingRepo);
  }, [deletingRepo, onDeletingRepoChange]);

  const clearImportPollTimer = useCallback(() => {
    if (importPollTimerRef.current) {
      clearTimeout(importPollTimerRef.current);
      importPollTimerRef.current = null;
    }
  }, []);

  const setImportPollTimer = useCallback(
    (importId) => {
      clearImportPollTimer();
      importPollTimerRef.current = setTimeout(() => {
        pollImportStatusRef.current?.(importId);
      }, IMPORT_POLL_INTERVAL_MS);
    },
    [clearImportPollTimer],
  );

  useEffect(
    () => () => {
      clearImportPollTimer();
    },
    [clearImportPollTimer],
  );

  const finishAction = useCallback(
    async (severity, message) => {
      setActionAlert({ severity, message });
      await loadStatus();
      await onRefreshModels();
    },
    [loadStatus, onRefreshModels],
  );

  const deleteModel = useCallback(
    async (deleteRepoId) => {
      if (!deleteRepoId || busyAction || deletingRepo) return;

      setBusyAction("delete");
      setDeletingRepo(deleteRepoId);
      setActionAlert(null);
      try {
        const payload = await deleteModelCache(deleteRepoId);
        await finishAction("success", formatDeleteModelResponse(payload));
        onModelDeleted(deleteRepoId);
      } catch (err) {
        console.error("Failed to delete model cache:", err);
        setActionAlert({
          severity: "error",
          message: err.message || "Failed to delete model cache.",
        });
        await loadStatus();
      } finally {
        setBusyAction("");
        setDeletingRepo("");
      }
    },
    [busyAction, deletingRepo, finishAction, loadStatus, onModelDeleted],
  );

  const finishImportStatus = useCallback(
    async (payload) => {
      clearImportPollTimer();
      if (payload?.state === "completed") {
        await finishAction("success", formatImportStatusResponse(payload));
      } else {
        setActionAlert({
          severity: "error",
          message: formatImportStatusResponse(payload),
        });
        await loadStatus();
      }
      setBusyAction("");
    },
    [clearImportPollTimer, finishAction, loadStatus],
  );

  const pollImportStatus = useCallback(
    async (importId) => {
      try {
        const payload = await getModelImportStatus(importId);
        if (payload?.state === "completed" || payload?.state === "failed") {
          await finishImportStatus(payload);
          return;
        }

        setActionAlert({
          severity: "info",
          message: formatImportStatusResponse(payload),
        });
        setImportPollTimer(importId);
      } catch (err) {
        console.error("Failed to poll model import status:", err);
        clearImportPollTimer();
        setActionAlert({
          severity: "error",
          message: err.message || "Failed to poll model import status.",
        });
        setBusyAction("");
        await loadStatus();
      }
    },
    [finishImportStatus, loadStatus, setImportPollTimer, clearImportPollTimer],
  );

  pollImportStatusRef.current = pollImportStatus;

  useImperativeHandle(
    ref,
    () => ({
      deleteModel,
    }),
    [deleteModel],
  );

  const handleImport = async (event) => {
    event.preventDefault();
    const trimmedRepoId = repoId.trim();
    const trimmedRevision = revision.trim() || "main";
    if (!trimmedRepoId || busyAction) return;

    setBusyAction("import");
    setActionAlert(null);
    clearImportPollTimer();
    try {
      const payload = await importModelCache({
        repoId: trimmedRepoId,
        revision: trimmedRevision,
        forceDownload,
      });
      setActionAlert({
        severity: "info",
        message: formatImportResponse(payload),
      });
      if (payload?.import_id) {
        setImportPollTimer(payload.import_id);
      } else {
        await finishAction("success", formatImportResponse(payload));
        setBusyAction("");
      }
    } catch (err) {
      console.error("Failed to import model:", err);
      setActionAlert({
        severity: "error",
        message: err.message || "Failed to import model.",
      });
      await loadStatus();
      setBusyAction("");
    }
  };

  const runConfirmedAction = async () => {
    if (!confirmAction || busyAction) return;

    const action = confirmAction;
    setConfirmAction(null);
    setBusyAction(action);
    setActionAlert(null);
    try {
      const payload =
        action === "scan" ? await scanModelCache() : await evictModelCache();
      await finishAction(
        "success",
        action === "scan"
          ? formatScanResponse(payload)
          : formatEvictionResponse(payload),
      );
    } catch (err) {
      console.error(`Failed to run model cache ${action}:`, err);
      setActionAlert({
        severity: "error",
        message: err.message || `Failed to run model cache ${action}.`,
      });
      await loadStatus();
    } finally {
      setBusyAction("");
    }
  };

  const inProgressText = status?.in_progress ? "In progress" : "Idle";

  return (
    <Box
      sx={{
        border: `1px solid ${HAIRLINE}`,
        borderRadius: `${RADIUS.md}px`,
        bgcolor: SURFACE,
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Stack gap={2}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: FONT.lg,
                fontWeight: WEIGHT.bold,
                color: INK,
              }}
            >
              Model cache admin
            </Typography>
            <Typography sx={{ fontSize: FONT.sm, color: INK_SECONDARY }}>
              Manager status and manual cache operations.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={loadStatus}
            disabled={statusLoading || Boolean(busyAction)}
            startIcon={
              statusLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <RefreshRoundedIcon sx={{ fontSize: 17 }} />
              )
            }
            sx={{ textTransform: "none" }}
          >
            Refresh
          </Button>
        </Box>

        {statusError && <Alert severity="warning">{statusError}</Alert>}
        {actionAlert && (
          <Alert
            severity={actionAlert.severity}
            onClose={() => setActionAlert(null)}
          >
            <AlertMessage message={actionAlert.message} />
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
            },
            gap: { xs: 2, md: 2.5 },
            alignItems: "start",
          }}
        >
          <Stack gap={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              <StatusField
                label="State"
                value={statusLoading ? "Loading" : inProgressText}
              />
              <StatusField
                label="Model list cache"
                value={formatDateTime(status?.last_model_list_cache_updated_at)}
              />
            </Box>

            <Box
              component="form"
              onSubmit={handleImport}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1fr) minmax(110px, 140px)",
                },
                gap: 1.25,
                alignItems: "center",
                minWidth: 0,
                "& .MuiFormControl-root": { minWidth: 0 },
              }}
            >
              <TextField
                size="small"
                value={repoId}
                onChange={(event) => setRepoId(event.target.value)}
                placeholder="organization/model"
                label="Repo ID"
                disabled={Boolean(busyAction)}
              />
              <TextField
                size="small"
                value={revision}
                onChange={(event) => setRevision(event.target.value)}
                label="Revision"
                disabled={Boolean(busyAction)}
              />
              <Box
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  minWidth: 0,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={forceDownload}
                      onChange={(event) =>
                        setForceDownload(event.target.checked)
                      }
                      disabled={Boolean(busyAction)}
                    />
                  }
                  label="Force download"
                  sx={{
                    color: INK_SECONDARY,
                    mr: 0,
                    "& .MuiFormControlLabel-label": { fontSize: FONT.sm },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!repoId.trim() || Boolean(busyAction)}
                  startIcon={
                    busyAction === "import" ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CloudDownloadRoundedIcon sx={{ fontSize: 17 }} />
                    )
                  }
                  sx={{ textTransform: "none", minHeight: 40 }}
                >
                  Import
                </Button>
              </Box>
            </Box>
          </Stack>

          <Stack gap={1.25}>
            <StatusField
              label="Last scan"
              value={formatDateTime(
                status?.last_scan_finished_at || status?.last_scan_started_at,
              )}
            />
            <Button
              variant="outlined"
              onClick={() => setConfirmAction("scan")}
              disabled={Boolean(busyAction)}
              startIcon={
                busyAction === "scan" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ManageSearchRoundedIcon sx={{ fontSize: 17 }} />
                )
              }
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Run scan
            </Button>
          </Stack>

          <Stack gap={1.25}>
            <StatusField
              label="Last eviction"
              value={formatDateTime(
                status?.last_eviction_finished_at ||
                  status?.last_eviction_started_at,
              )}
            />
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmAction("evict")}
              disabled={Boolean(busyAction)}
              startIcon={
                busyAction === "evict" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteSweepRoundedIcon sx={{ fontSize: 17 }} />
                )
              }
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Run eviction
            </Button>
          </Stack>
        </Box>

        {status?.last_error && (
          <Alert severity="error">{status.last_error}</Alert>
        )}
      </Stack>

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
      >
        <DialogTitle>
          {confirmAction === "scan" ? "Run model cache scan?" : "Run eviction?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === "scan"
              ? "This will scan local model cache paths and import discovered models into the cache index."
              : "This will evict cached model revisions until the configured target size is met."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmAction(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmAction === "evict" ? "error" : "primary"}
            onClick={runConfirmedAction}
            sx={{ textTransform: "none" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ModelAdminPanel.propTypes = {
  onRefreshModels: PropTypes.func.isRequired,
  refreshSignal: PropTypes.number,
  onModelDeleted: PropTypes.func,
  onDeletingRepoChange: PropTypes.func,
};

export default ModelAdminPanel;
